import { apiClient } from './client';

// Mirrors packages/shared/src/types/web-analytics.ts (web app keeps its own copy, as gtm.ts does).

export interface WebAnalyticsOverview {
  visitors: number;
  sessions: number;
  identifiedVisitors: number;
  identifiedCompanies: number;
  events: number;
  /** Percent change vs the preceding equal-length period (0 when no baseline). */
  visitorsDelta: number;
  sessionsDelta: number;
  identifiedVisitorsDelta: number;
  identifiedCompaniesDelta: number;
  eventsDelta: number;
}

export interface WebAnalyticsTrendPoint {
  /** YYYY-MM-DD. */
  date: string;
  visitors: number;
  sessions: number;
  identifications: number;
}

export interface WebAnalyticsChannelRow {
  channel: string;
  source: string;
  medium: string;
  sessions: number;
  visitors: number;
}

export interface WebAnalyticsPageRow {
  page: string;
  pageviews: number;
  visitors: number;
}

export interface WebAnalyticsHealth {
  lastEventAt: string | null;
  eventsLast24h: number;
  avgPerDay7d: number;
  stale: boolean;
  staleThresholdHours: number;
}

/** Cookie consent: tracker visitors against every real browser CloudFront saw, on the days it has a count. */
export interface WebAnalyticsConsent {
  coveredDays: number;
  periodDays: number;
  allBrowsers: number;
  consentedVisitors: number;
  rate: number | null;
  singlePageBrowsers: number;
  mobileBrowsers: number;
  byChannel: Array<{ channel: string; browsers: number; consented: number }>;
  daily: Array<{ date: string; browsers: number; consented: number }>;
}

export interface WebAnalyticsData {
  period: { startDate: string; endDate: string };
  overview: WebAnalyticsOverview | null;
  trend: WebAnalyticsTrendPoint[] | null;
  channels: WebAnalyticsChannelRow[] | null;
  topPages: WebAnalyticsPageRow[] | null;
  health: WebAnalyticsHealth | null;
  /** GA4 sessions over the same days (the Overview funnel's figure); null when unavailable. */
  ga4Sessions?: number | null;
  consent?: WebAnalyticsConsent | null;
  unavailable: string[];
}

/** The days a Website list covers, inclusive (YYYY-MM-DD). */
export interface ListPeriod {
  startDate: string;
  endDate: string;
}

export async function getWebAnalytics(startDate?: string, endDate?: string): Promise<WebAnalyticsData> {
  return apiClient.get<WebAnalyticsData>('/web-analytics', {
    params: { startDate, endDate },
  });
}

// --- Journeys (identified visitors) ---------------------------------------

export interface JourneyListRow {
  email: string;
  /** From the form, else the Attio person; null when neither is known. */
  fullName: string | null;
  attioUrl: string | null;
  companyName: string | null;
  firstSeenAt: string;
  lastSeenAt: string;
  pageviews: number;
  activeSeconds: number;
  forms: number;
}

export type JourneyTimelineItem =
  | { kind: 'page'; at: string; path: string; activeSeconds: number | null; scrollPct: number | null }
  | { kind: 'click'; at: string; label: string; cta: string | null }
  | { kind: 'form'; at: string; step: 'viewed' | 'started' | 'submitted'; formId: string | null }
  | { kind: 'conversion'; at: string; formName: string | null };

export interface JourneyVisit {
  startedAt: string;
  channel: string;
  source: string;
  items: JourneyTimelineItem[];
}

export async function getJourneys(period: ListPeriod): Promise<{ journeys: JourneyListRow[] }> {
  return apiClient.get('/web-analytics/journeys', { params: { ...period } });
}

export async function getJourney(email: string): Promise<{ email: string; visits: JourneyVisit[] }> {
  return apiClient.get(`/web-analytics/journeys/${encodeURIComponent(email)}`);
}

// --- Visiting companies and RB2B-identified people --------------------------

export interface VisitingCompanyRow {
  domain: string;
  name: string;
  industry: string | null;
  employeeCount: string | null;
  /** RB2B's company location ("Deurne, Antwerp"), often the HQ, not where the visitor was. */
  location: string | null;
  /** One per visitor per day, as the company timeline counts them. */
  visits: number;
  /** RB2B page views (each page view is its own RB2B row). */
  pageViews: number;
  /** Pricing or demo-request pages viewed in the period. */
  intentPages: string[];
  /** Attio Industry / ICP when the company is in Attio with a category; else 50+ staff by RB2B, schools left out. */
  icpFit: boolean;
  /** How icpFit was decided: Attio's category, or RB2B size because Attio can't place the company. */
  icpBasis?: 'attio' | 'size';
  people: number;
  topPages: string[];
  lastVisitAt: string | null;
  attioUrl: string | null;
  linkedinImpressions: number;
  lastOutboundAt: string | null;
}

export interface IdentifiedPersonRow {
  email: string;
  fullName: string | null;
  title: string | null;
  companyName: string | null;
  linkedinUrl: string | null;
  visits: number;
  lastSeenAt: string | null;
  pages: Array<{ at: string; path: string }>;
}

export async function getVisitingCompanies(
  period: ListPeriod,
): Promise<{ companies: VisitingCompanyRow[]; leftOut?: { nonBuyers?: string[] }; identifiedTotal?: number | null }> {
  return apiClient.get('/web-analytics/companies', { params: { ...period } });
}

export async function getIdentifiedPeople(period: ListPeriod): Promise<{ people: IdentifiedPersonRow[]; total?: number | null }> {
  return apiClient.get('/web-analytics/identified-people', { params: { ...period } });
}

export interface CompanyTimelineItem {
  at: string;
  who: string | null;
  text: string;
}

export async function getCompanyTimeline(domain: string): Promise<{ domain: string; items: CompanyTimelineItem[] }> {
  return apiClient.get(`/web-analytics/companies/${encodeURIComponent(domain)}/timeline`);
}

// --- LinkedIn lead gen forms ------------------------------------------------

export type LinkedInLeadAttioStatus = 'added' | 'notion' | 'no_email' | 'pending';

export interface LinkedInLeadRow {
  id: string;
  submittedAt: string;
  name: string | null;
  email: string | null;
  formName: string | null;
  event: string | null;
  attioStatus: LinkedInLeadAttioStatus;
  attioUrl: string | null;
}

export async function getLinkedInLeads(period: ListPeriod): Promise<{ leads: LinkedInLeadRow[] }> {
  return apiClient.get('/web-analytics/linkedin-leads', { params: { ...period } });
}
